import { useEffect, useState } from 'react';

export default function QuestionFormModal({ question, isNew, onSave, onClose }) {
  const [draft, setDraft] = useState({
    id: question?.id || `q-${Date.now()}`,
    text: question?.questionText || question?.text || '',
    type: question?.type || 'mcq',
    options: question?.options || (question?.type === 'true-false' ? ['True', 'False'] : ['', '', '', '']),
    correctAnswer: question?.correctAnswer ?? 0,
    explanation: question?.explanation || '',
  });

  // Sync when question prop changes (important for editing)
  useEffect(() => {
    if (question) {
      setDraft({
        id: question.id || `q-${Date.now()}`,
        text: question.questionText || question.text || '',
        type: question.type || 'mcq',
        options: question.options || (question.type === 'true-false' ? ['True', 'False'] : ['', '', '', '']),
        correctAnswer: question.correctAnswer ?? 0,
        explanation: question.explanation || '',
      });
    }
  }, [question]);

  const handleTypeChange = (type) => {
    const options = type === 'true-false' ? ['True', 'False'] : ['', '', '', ''];
    setDraft(prev => ({ 
      ...prev, 
      type, 
      options, 
      correctAnswer: 0 
    }));
  };

  const updateOption = (idx, value) => {
    const options = [...draft.options];
    options[idx] = value;
    setDraft(prev => ({ ...prev, options }));
  };

  const handleSave = () => {
    if (!draft.text.trim()) return;

    // Convert to backend format
    const questionForBackend = {
      questionText: draft.text.trim(),
      options: draft.options,
      correctAnswer: Number(draft.correctAnswer),
      explanation: draft.explanation.trim(),
    };

    onSave(questionForBackend);
  };

 return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">{isNew ? 'Add Question' : 'Edit Question'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer rounded-lg hover:bg-slate-100">
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Question Type */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-2">Question Type</label>
            <div className="flex gap-2">
              {(['mcq', 'true-false']).map(type => (
                <button
                  key={type}
                  onClick={() => handleTypeChange(type)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                    draft.type === type
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'text-slate-600 border-slate-200 hover:border-teal-300 bg-white'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={type === 'mcq' ? 'ri-list-check' : 'ri-toggle-line'} />
                  </div>
                  {type === 'mcq' ? 'Multiple Choice' : 'True / False'}
                </button>
              ))}
            </div>
          </div>

          {/* Question Text */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">Question *</label>
            <textarea
              value={draft.text}
              onChange={e => setDraft(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Enter your question here..."
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 resize-none"
            />
          </div>

          {/* Options */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-2">
              {draft.type === 'true-false' ? 'Options' : 'Answer Options *'}
            </label>
            <div className="space-y-2">
              {draft.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2.5">
                  <button
                    onClick={() => setDraft(prev => ({ ...prev, correctAnswer: idx }))}
                    className={`w-6 h-6 flex items-center justify-center rounded-full border-2 flex-shrink-0 cursor-pointer transition-colors ${
                      draft.correctAnswer === idx
                        ? 'border-teal-500 bg-teal-500'
                        : 'border-slate-300 hover:border-teal-300'
                    }`}
                  >
                    {draft.correctAnswer === idx && <i className="ri-check-line text-white text-xs" />}
                  </button>
                  <input
                    type="text"
                    value={opt}
                    onChange={e => updateOption(idx, e.target.value)}
                    disabled={draft.type === 'true-false'}
                    placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none transition-colors ${
                      draft.type === 'true-false' 
                        ? 'bg-slate-50 text-slate-500 border-slate-200' 
                        : draft.correctAnswer === idx
                        ? 'border-teal-300 bg-teal-50 focus:border-teal-400'
                        : 'border-slate-200 focus:border-teal-400'
                    }`}
                  />
                  {draft.correctAnswer === idx && (
                    <span className="text-xs text-teal-600 font-semibold whitespace-nowrap">Correct</span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">Click the circle to mark the correct answer</p>
          </div>

          {/* Explanation */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">Explanation (Optional)</label>
            <textarea
              value={draft.explanation}
              onChange={e => setDraft(prev => ({ ...prev, explanation: e.target.value }))}
              placeholder="Explain why this is the correct answer..."
              rows={2}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg cursor-pointer hover:bg-slate-50 whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!draft.text?.trim()}
            className="px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg cursor-pointer hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap"
          >
            {isNew ? 'Add Question' : 'Save Question'}
          </button>
        </div>
      </div>
    </div>
  );
}
